import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, Target, Loader2, Calendar, Globe } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, subDays } from 'date-fns';
import { trackPageView } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState(null);
  const [conversionSources, setConversionSources] = useState(null);
  const [usageData, setUsageData] = useState(null);
  
  // Date range filter (default: last 7 days)
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedCountry, setSelectedCountry] = useState('');

  useEffect(() => {
    trackPageView('analytics');
    
    // Check if user is admin
    const facilitator = JSON.parse(localStorage.getItem('facilitator') || '{}');
    if (!facilitator.is_admin) {
      toast.error('Admin access required');
      navigate('/profile');
      return;
    }
    
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = {
        start_date: startDate,
        end_date: endDate,
      };
      
      if (selectedCountry) {
        params.country = selectedCountry;
      }

      // Fetch all analytics data
      const [funnelRes, sourcesRes, usageRes] = await Promise.all([
        axios.get(`${API}/analytics/funnel`, { headers, params }),
        axios.get(`${API}/analytics/conversion-sources`, { headers, params }),
        axios.get(`${API}/analytics/usage`, { headers, params })
      ]);

      setFunnelData(funnelRes.data);
      setConversionSources(sourcesRes.data);
      setUsageData(usageRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchAnalyticsData();
  };

  const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Loader2 className="h-12 w-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Prepare funnel chart data
  const funnelChartData = funnelData ? [
    { stage: 'Landing Page', visitors: funnelData.landing_page_unique_visitors, percentage: 100 },
    { stage: 'Features View', visitors: funnelData.features_views, percentage: funnelData.features_view_rate },
    { stage: 'Use Cases View', visitors: funnelData.use_cases_views, percentage: funnelData.use_cases_view_rate },
    { stage: 'Pricing View', visitors: funnelData.pricing_views, percentage: funnelData.pricing_view_rate },
    { stage: 'Sign In/Up', visitors: funnelData.sign_in_clicks + funnelData.sign_up_clicks, percentage: ((funnelData.sign_in_clicks + funnelData.sign_up_clicks) / funnelData.landing_page_unique_visitors * 100).toFixed(2) },
    { stage: 'Successful Auth', visitors: funnelData.successful_sign_ins + funnelData.successful_sign_ups, percentage: ((funnelData.successful_sign_ins + funnelData.successful_sign_ups) / (funnelData.sign_in_clicks + funnelData.sign_up_clicks) * 100).toFixed(2) },
    { stage: 'Subscribed', visitors: funnelData.subscription_success, percentage: funnelData.subscription_rate }
  ] : [];

  // Prepare conversion sources pie chart data
  const sourcesChartData = conversionSources ? Object.entries(conversionSources.sources).map(([name, data]) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: data.conversions,
    views: data.views,
    rate: data.rate
  })).filter(item => item.value > 0) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/profile')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Profile
              </Button>
              <PollarBrandMini />
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="country">Country (Optional)</Label>
                <Input
                  id="country"
                  type="text"
                  placeholder="e.g., US, GB"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value.toUpperCase())}
                  className="mt-1"
                  maxLength={2}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleApplyFilters} className="w-full">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Landing Page Visitors</p>
                  <p className="text-3xl font-bold text-indigo-600">{funnelData?.landing_page_unique_visitors || 0}</p>
                </div>
                <Users className="h-10 w-10 text-indigo-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Sign Up Success Rate</p>
                  <p className="text-3xl font-bold text-purple-600">{funnelData?.sign_up_rate.toFixed(1) || 0}%</p>
                </div>
                <Target className="h-10 w-10 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Subscriptions</p>
                  <p className="text-3xl font-bold text-pink-600">{funnelData?.subscription_success || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-pink-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Subscription Rate</p>
                  <p className="text-3xl font-bold text-green-600">{funnelData?.subscription_rate.toFixed(1) || 0}%</p>
                </div>
                <Globe className="h-10 w-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Funnel Dashboard */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={funnelChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="stage" angle={-15} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'percentage') return `${value}%`;
                    return value;
                  }}
                />
                <Legend />
                <Bar dataKey="visitors" fill="#8b5cf6" name="Visitors" />
                <Bar dataKey="percentage" fill="#ec4899" name="Conversion %" />
              </BarChart>
            </ResponsiveContainer>
            
            {/* Funnel Stats */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Features View Rate</p>
                <p className="text-2xl font-bold text-purple-600">{funnelData?.features_view_rate.toFixed(1)}%</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-gray-600">Pricing View Rate</p>
                <p className="text-2xl font-bold text-indigo-600">{funnelData?.pricing_view_rate.toFixed(1)}%</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-sm text-gray-600">Sign In Success</p>
                <p className="text-2xl font-bold text-pink-600">{funnelData?.sign_in_rate.toFixed(1)}%</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Sign Up Success</p>
                <p className="text-2xl font-bold text-green-600">{funnelData?.sign_up_rate.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Trend */}
        {funnelData?.daily_breakdown && funnelData.daily_breakdown.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Daily Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={funnelData.daily_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="landing_views" stroke="#8b5cf6" name="Landing Views" />
                  <Line type="monotone" dataKey="sign_ups" stroke="#10b981" name="Sign Ups" />
                  <Line type="monotone" dataKey="subscriptions" stroke="#f59e0b" name="Subscriptions" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Conversion Sources */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Subscription Conversion Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sourcesChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourcesChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Stats Table */}
              <div className="space-y-3">
                {sourcesChartData.map((source, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div>
                        <p className="font-medium">{source.name}</p>
                        <p className="text-sm text-gray-500">{source.views} views</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{source.value}</p>
                      <Badge variant="secondary">{source.rate}% CVR</Badge>
                    </div>
                  </div>
                ))}
                
                {sourcesChartData.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No conversion data available</p>
                )}
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total Subscription Page Views</p>
                  <p className="text-2xl font-bold text-indigo-600">{conversionSources?.total_subscription_page_views || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Conversions</p>
                  <p className="text-2xl font-bold text-green-600">{conversionSources?.total_conversions || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Overall CVR</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {conversionSources?.total_subscription_page_views > 0 
                      ? ((conversionSources.total_conversions / conversionSources.total_subscription_page_views) * 100).toFixed(1)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Feature Usage Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Page Views */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Page Views</h3>
                <div className="space-y-2">
                  {usageData?.page_views.slice(0, 5).map((stat, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{stat.event_type.replace(/_/g, ' ')}</span>
                      <div className="flex gap-4">
                        <span className="text-sm font-medium">{stat.count} views</span>
                        <span className="text-sm text-gray-500">{stat.unique_users} users</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* User Actions */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Top User Actions</h3>
                <div className="space-y-2">
                  {usageData?.user_actions.slice(0, 5).map((stat, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{stat.event_type.replace(/_/g, ' ')}</span>
                      <div className="flex gap-4">
                        <span className="text-sm font-medium">{stat.count} actions</span>
                        <span className="text-sm text-gray-500">{stat.unique_users} users</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Session Activity */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Session Activity</h3>
                <div className="space-y-2">
                  {usageData?.session_activity.slice(0, 5).map((stat, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{stat.event_type.replace(/_/g, ' ')}</span>
                      <div className="flex gap-4">
                        <span className="text-sm font-medium">{stat.count} events</span>
                        <span className="text-sm text-gray-500">{stat.unique_users} users</span>
                      </div>
                    </div>
                  ))}
                  {usageData?.session_activity.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No session activity yet</p>
                  )}
                </div>
              </div>
              
              {/* Subscription Activity */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Subscription Activity</h3>
                <div className="space-y-2">
                  {usageData?.subscription_activity.slice(0, 5).map((stat, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{stat.event_type.replace(/_/g, ' ')}</span>
                      <div className="flex gap-4">
                        <span className="text-sm font-medium">{stat.count} events</span>
                        <span className="text-sm text-gray-500">{stat.unique_users} users</span>
                      </div>
                    </div>
                  ))}
                  {usageData?.subscription_activity.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No subscription activity yet</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
